import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { shallowArrayEquals } from './utility';
import { queryVms, resolveQueryContainer } from './vmsQuery';

/**
 * Wraps a component to have it receive data from the VMS.
 *
 * Reads the component's `count` property to determine how many items to
 * initially load. Defaults to 1.
 *
 * The wrapped component will receive all state props. Refer to the state props
 * for more details. Additionally, it will receive the following props:
 * `loadMore(count)` - function - loads `count` more items if available
 *
 * The `propMapping` function facilitates mapping keys that make sense for your
 * component to keys that the VMS understands. Commonly used params include:
 * `guid` - VMS unique identifier
 * `origin` - brand source of content
 * `ordering` - 'reference_date' (date), '-updated_date' (for PrimeTime Shows) or undefined/null (collection's order).
 * `type` - type of container ('season', 'series', 'media', 'episode', ...)
 * `primary_parent` - associated series GUID (usually - see VMS docs)
 * See the VMS docs for more options:
 * https://github.com/ShawONEX/vms-content-service
 *
 * The component can pass in, via props, a function `resultsFilter()` to filter
 * the results from VMS.
 *
 * @param {Component|function} WrappedComponent The component needing VMS data.
 * @param {function} propMapping A function accepting `props` and returning an
 *   object of the form { [vmsKey]: value }.
 * @param {boolean} propagateErrors Whether to throw an error up the tree when
 *   the VMS call fails for some reason. Default false (sets errorState: true instead).
 */
const fromVms = (WrappedComponent, propMapping, propagateErrors = false) => {
	const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

	return class extends Component {
		static displayName = `FromVms(${wrappedComponentName})`;

		static propTypes = {
			count: PropTypes.number,
			resultsFilter: PropTypes.func,
		}

		static defaultProps = {
			count: 0,
			resultsFilter: null,
		}

		// static contextType = UserContext;

		constructor(props) {
			super(props);

			this.state = {
				/** Whether more items are available for the query. */
				canLoadMore: false,
				/** Whether an error occurred during loading. */
				errorState: false,
				/** The resulting items from the VMS query. */
				items: [],
				/** Whether the VMS data is still loading. */
				loading: true,
				/** The total/maximum number of items attempted to be loaded. */
				targetCount: props.count || propMapping(props).count || 1,
				/** The number of query containers still needing resolution. */
				outstandingQueryContainers: 0,
			};
		}

		/**
		 * React life cycle function. Called by React when the component is mounted (rendered/created). Can be used to
		 * perform non-pure actions instead of render(). A single call to render() happens before this, and render() is NOT
		 * called automatically after this function ends.
		 * @returns {void}
		 */
		componentDidMount() {
			const mappedProps = propMapping(this.props);
			this.loadData(mappedProps);
		}

		/**
		 * React life cycle function. Called by React when the component is updated.
		 * Use this as an opportunity to operate on the DOM when the component has been updated.
		 * @returns {void}
		 */
		componentDidUpdate(oldProps) {
			let requiresUpdate = false;

			const prevMappedProps = propMapping(oldProps);
			const mappedProps = propMapping(this.props);

			// eslint-disable-next-line no-restricted-syntax
			for (const [key, value] of Object.entries(mappedProps)) {
				const oldValue = prevMappedProps[key];

				if (Array.isArray(value) && Array.isArray(oldValue)) {
					if (!shallowArrayEquals(value, oldValue)) {
						requiresUpdate = true;
					}
				} else if (value !== oldValue) {
					requiresUpdate = true;
				}
			}

			if (requiresUpdate) {
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState(
					{
						items: [],
						loading: true,
					},
					() => this.loadData(mappedProps),
				);
			}
		}

		/**
		 * Load data from the VMS API. Called on component mount, and again when props change.
		 */
		loadData(props) {
			const { targetCount, items } = this.state;
			if (!props.limit) {
				// eslint-disable-next-line no-param-reassign
				props.limit = targetCount - items.length;
			}

			// Query container support: always add 'query' to specified types, if any
			if (props.type) {
				// eslint-disable-next-line no-param-reassign
				props.type = ['query'].concat(props.type);
			}

			queryVms(props).then(
				(data) => this.finishLoading(data, props),
				(reason) => this.errorState(reason),
			);
		}

		/**
		 * Updates the state once data has been retrieved.
		 * @param {Object} data The JSON API response from the VMS.
		 */
		finishLoading(data, mappedProps) {
			if (!data) return;

			let outstandingQueryContainers = 0;
			let newItems = [];

			// If an array of GUIDs is requested, ensure that items are returned in the order they were requested.
			// Ideally, this could be done in the VMS.
			if (!mappedProps.ordering && Array.isArray(mappedProps.guid) && mappedProps.guid.length > 1) {
				mappedProps.guid.forEach((guid) => {
					data.results.forEach((result) => {
						if (result.guid === guid) {
							newItems.push(result);
						}
					});
				});
			} else {
				newItems = data.results;
			}

			// Check for presence of query containers in resulting data.
			if (newItems && newItems.length > 0) {
				// eslint-disable-next-line no-restricted-syntax
				for (const result of newItems) {
					if (result.type === 'query') {
						outstandingQueryContainers += 1;
						resolveQueryContainer(result.data).then(
							(innerData) => this.populateQueryContainer(result.guid, innerData.results),
							(reason) => this.errorState(reason),
						);
					}
				}
			}

			const { resultsFilter } = this.props;
			const { items } = this.state;

			// Apply an extra filter to results, if specified
			if (resultsFilter) {
				newItems = resultsFilter(newItems);
			}

			this.setState({
				canLoadMore: !!data.next,
				items: items.concat(newItems),
				loading: outstandingQueryContainers !== 0,
				outstandingQueryContainers,
			});
		}

		/**
		 * Callback to populate a finished-executing query container.
		 * @param {string} queryGuid The GUID of the finished query contiainer, to be replaced.
		 * @param {Array} data The resulting items to swap in.
		 */
		populateQueryContainer(queryGuid, data) {
			const { items } = this.state;
			let { outstandingQueryContainers } = this.state;
			let newItems = items;

			// eslint-disable-next-line no-restricted-syntax
			for (const index in items) {
				// Find the query container and replace it with the resulting data.
				if (items[index].guid === queryGuid) {
					newItems = [...items.slice(0, index - 1), ...data, ...items.slice(index + 1)];
				}
			}

			outstandingQueryContainers -= 1;

			this.setState({
				items: newItems,
				loading: outstandingQueryContainers !== 0,
				outstandingQueryContainers,
			});
		}

		/**
		 * Sets the component to an error state.
		 * @param {any} reason The cause of the error.
		 */
		errorState(reason) {
			this.setState({
				loading: false,
				errorState: reason,
			});
			console.error(reason);
		}

		/**
		 * Load the next N items from the VMS.
		 * @param {number} count How many more items to load.
		 */
		loadMore(count) {
			const { targetCount } = this.state;
			this.setState({ targetCount: targetCount + count }, () => this.loadData());
		}

		render() {
			const { errorState } = this.state;
			if (errorState && propagateErrors) {
				throw errorState;
			}
			// eslint-disable-next-line react/jsx-props-no-spreading
			return <WrappedComponent loadMore={this.loadMore} {...this.state} {...this.props} />;
		}
	};
};

export default fromVms;
